import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { api } from '../../helpers/apiUtils';
import SamHtmlPreviewDialog, { SamHtmlPreviewData } from './SamHtmlPreviewDialog';
import UnifiedPlanBlock, { PlanResponse } from './UnifiedPlanBlock';
import UnifiedOwnershipBlock, { OwnershipResponse } from './UnifiedOwnershipBlock';
import UnifiedDedupHealthBlock, { DedupHealthResponse } from './UnifiedDedupHealthBlock';
import UnifiedOverlapBlock, { OverlapResponse } from './UnifiedOverlapBlock';

/**
 * Unified SAM cockpit — evaluation view for the one-VNR-SAM-per-user-per-day
 * system (LIVE 2026-07-17). Four blocks fed by /api/adserver/sam-unified/*:
 * daily plan + per-client execution, ownership distribution, dedup-health
 * trend (persisted nightly verification), overlap economics.
 */

// Berlin "today" via local date components — .toISOString() would flip the
// date around midnight DST edges (see react rules § date handling).
const berlinToday = (): string => {
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return parts; // sv-SE locale formats as YYYY-MM-DD
};

const UnifiedTab: React.FC = () => {
  const [date, setDate] = useState<string>(berlinToday());
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [ownership, setOwnership] = useState<OwnershipResponse | null>(null);
  const [overlap, setOverlap] = useState<OverlapResponse | null>(null);
  const [health, setHealth] = useState<DedupHealthResponse | null>(null);
  const [loadingDated, setLoadingDated] = useState(false);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [preview, setPreview] = useState<SamHtmlPreviewData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Plan + ownership + overlap follow the selected date.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDated(true);
      setError(null);
      try {
        const [planRes, ownRes, ovRes] = await Promise.all([
          api.get(`/api/adserver/sam-unified/plan?date=${date}`),
          api.get(`/api/adserver/sam-unified/ownership?date=${date}`),
          api.get(`/api/adserver/sam-unified/overlap?date=${date}&days=30`),
        ]);
        if (cancelled) return;
        setPlan(planRes.data?.data ?? null);
        setOwnership(ownRes.data?.data ?? null);
        setOverlap(ovRes.data?.data ?? null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoadingDated(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date]);

  // Dedup-health trend is date-independent (rolling 30d window).
  const loadHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      const { data } = await api.get('/api/adserver/sam-unified/dedup-health?days=30');
      setHealth(data?.data ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingHealth(true);
      try {
        const { data } = await api.get('/api/adserver/sam-unified/dedup-health?days=30');
        if (!cancelled) setHealth(data?.data ?? null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoadingHealth(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePreview = async (id: number) => {
    setError(null);
    try {
      const { data } = await api.get(`/api/adserver/external-sams/preview/${id}`);
      if (data?.success) {
        setPreview(data.data as SamHtmlPreviewData);
        setPreviewOpen(true);
      } else {
        setError(data?.error ?? 'Vorschau fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          type="date"
          label="Datum"
          InputLabelProps={{ shrink: true }}
          value={date}
          onChange={(e) => e.target.value && setDate(e.target.value)}
        />
        <Button size="small" onClick={() => setDate(berlinToday())}>
          Heute
        </Button>
        <Button size="small" onClick={loadHealth}>
          Aktualisieren
        </Button>
        <Typography variant="caption" sx={{ color: '#555' }}>
          Ownership-Daten (sam_dedup_assignments) reichen max. 90 Tage zurück.
        </Typography>
      </Box>

      <UnifiedPlanBlock data={plan} loading={loadingDated} onPreview={handlePreview} />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Box sx={{ flex: '1 1 420px', minWidth: 380 }}>
          <UnifiedOwnershipBlock data={ownership} loading={loadingDated} />
        </Box>
        <Box sx={{ flex: '1 1 420px', minWidth: 380 }}>
          <UnifiedDedupHealthBlock data={health} loading={loadingHealth} />
        </Box>
      </Box>

      <UnifiedOverlapBlock data={overlap} loading={loadingDated} />

      <SamHtmlPreviewDialog
        open={previewOpen}
        preview={preview}
        onClose={() => setPreviewOpen(false)}
      />
    </Box>
  );
};

export default UnifiedTab;
